import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import FeatureCard from '../components/AISubscription/FeatureCard';
import UsageDashboard from '../components/AISubscription/UsageDashboard';
import UpgradeModal from '../components/AISubscription/UpgradeModal';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import { FaBrain, FaRocket } from 'react-icons/fa';

/**
 * AI Features Hub Page
 * Main dashboard for all AI-powered features
 * Follows Single Responsibility Principle - manages feature discovery and access
 */
const AIFeaturesHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    subscription, 
    usageStats, 
    checkFeatureAccess, 
    getTierInfo, 
    loading,
    fetchCurrentSubscription,
    fetchUsageStats
  } = useSubscription();
  
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);

  // Force refresh subscription data on mount (in case user just purchased)
  useEffect(() => {
    window.scrollTo(0, 0);
    console.log('AIFeaturesHub mounted, subscription:', subscription);
    
    if (user?.id) {
      console.log('Refreshing subscription data...');
      fetchCurrentSubscription();
      fetchUsageStats();
    }
  }, [user, fetchCurrentSubscription, fetchUsageStats]);

  // Debug log when subscription changes
  useEffect(() => {
    console.log('Subscription updated in AIFeaturesHub:', subscription);
  }, [subscription]);

  // AI Features configuration
  const aiFeatures = [
    {
      id: 'quiz_generator',
      name: 'AI Quiz Generator',
      description: 'Generate custom quizzes on any topic with AI. Adaptive difficulty and instant feedback.',
      icon: '📝',
      tier: 'FREE',
      route: '/ai/quiz-generator',
      usageKey: 'quiz_generator',
      ctaText: 'Generate Quiz',
    },
    {
      id: 'doubt_solver',
      name: 'AI Doubt Solver',
      description: 'Get instant answers to your questions. Supports text, image, and voice input.',
      icon: '❓',
      tier: 'BASIC',
      route: '/ai/doubt-solver',
      usageKey: 'doubt_solver',
      ctaText: 'Ask Doubt',
    },
    {
      id: 'notes_summarizer',
      name: 'Notes Summarizer',
      description: 'Convert lengthy documents into concise, structured summaries with key concepts.',
      icon: '📄',
      tier: 'FREE',
      route: '/ai/summarizer',
      usageKey: 'notes_generator',
      ctaText: 'Summarize Notes',
    },
    {
      id: 'flashcard_generator',
      name: 'Flashcard Generator',
      description: 'Automatically create flashcard sets from study materials for spaced repetition learning.',
      icon: '🎴',
      tier: 'BASIC',
      route: '/ai/flashcards',
      usageKey: 'flashcard_generator',
      ctaText: 'Create Flashcards',
    },
    {
      id: 'study_planner',
      name: 'AI Study Planner',
      description: 'Personalized study schedules based on your courses, deadlines, and learning patterns.',
      icon: '📅',
      tier: 'BASIC',
      route: '/ai/study-planner',
      usageKey: 'study_planner',
      ctaText: 'Create Plan',
    },
    {
      id: 'mock_test',
      name: 'Mock Test Generator',
      description: 'Full-length practice tests simulating real exams with detailed performance analysis.',
      icon: '📊',
      tier: 'PREMIUM',
      route: '/ai/mock-test',
      usageKey: 'mock_test_generator',
      ctaText: 'Generate Test',
    },
    {
      id: 'learning_path',
      name: 'Personalized Learning Path',
      description: 'AI-driven curriculum recommendations based on your strengths and weaknesses.',
      icon: '🎯',
      tier: 'PREMIUM',
      route: '/ai/learning-path',
      usageKey: 'learning_path',
      ctaText: 'View Path',
    },
    {
      id: 'video_summarizer',
      name: 'Video Summarizer',
      description: 'Extract key points from lectures and educational videos with timestamped notes.',
      icon: '🎥',
      tier: 'PREMIUM',
      route: '/ai/video-summarizer',
      usageKey: 'video_summary',
      ctaText: 'Summarize Video',
    },
    {
      id: 'concept_explainer',
      name: 'Concept Explainer',
      description: 'Deep dive explanations with analogies, examples, and visualizations for complex topics.',
      icon: '💡',
      tier: 'PREMIUM',
      route: '/ai/concept-explainer',
      usageKey: 'concept_explain',
      ctaText: 'Explain Concept',
    },
  ];

  // Check if feature is locked based on subscription tier
  const isFeatureLocked = (featureTier) => {
    const tierInfo = getTierInfo();
    const currentTier = (tierInfo.tier || 'free').toLowerCase(); // Normalize to lowercase
    const requiredTier = featureTier.toLowerCase(); // Normalize to lowercase
    
    console.log('Feature Access Check:', {
      featureTier,
      requiredTier,
      currentTier,
      tierInfo,
      subscription
    });
    
    // Free features are accessible to everyone
    if (requiredTier === 'free') return false;
    
    // Basic features are accessible to basic and premium users
    if (requiredTier === 'basic' && (currentTier === 'basic' || currentTier === 'premium')) return false;
    
    // Premium features are accessible to premium users only
    if (requiredTier === 'premium' && currentTier === 'premium') return false;
    
    return true;
  };

  // Get usage info for a feature
  const getUsageInfo = (usageKey) => {
    if (!usageStats) return null;
    
    const used = usageStats[`${usageKey}_used`] || 0;
    const limit = usageStats[`${usageKey}_limit`] || 0;
    const percentage = limit > 0 ? (used / limit) * 100 : 0;
    
    return { used, limit, percentage };
  };

  // Handle feature click
  const handleFeatureClick = async (feature) => {
    const isLocked = isFeatureLocked(feature.tier);
    
    console.log('Feature Click:', {
      feature: feature.name,
      featureTier: feature.tier,
      isLocked,
      subscription
    });
    
    if (isLocked) {
      setSelectedFeature(feature);
      setShowUpgradeModal(true);
      return;
    }

    // Check usage limits
    const access = await checkFeatureAccess(feature.usageKey);
    
    console.log('Feature Access Result:', access);
    
    if (!access.has_access) {
      setSelectedFeature(feature);
      setShowUpgradeModal(true);
      return;
    }

    // Navigate to feature page
    navigate(feature.route);
  };

  // Handle upgrade click
  const handleUpgrade = () => {
    navigate('/subscription/plans');
  };

  if (loading) {
    return (
      <div>
        <PublicHeader />
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" text="Loading AI Features..." />
        </div>
        <Footer />
      </div>
    );
  }

  const tierInfo = getTierInfo();

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />
      
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        {/* Hero Section - Mobile Optimized */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-12 text-white mb-4 sm:mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4">
            <FaBrain className="text-4xl sm:text-5xl flex-shrink-0" />
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">AI Study Tools</h1>
              <p className="text-purple-100 mt-1 sm:mt-2 text-sm sm:text-base">
                Supercharge your learning with AI-powered features
              </p>
            </div>
          </div>
          
          {/* Current Plan Badge - Mobile Stacked */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mt-4 sm:mt-6">
            <div className="bg-white bg-opacity-20 backdrop-blur px-4 py-2 rounded-lg text-center sm:text-left">
              <span className="text-sm font-semibold">Current Plan: {tierInfo.name}</span>
            </div>
            {tierInfo.name === 'Free' && (
              <button
                onClick={handleUpgrade}
                className="bg-white text-purple-600 px-6 py-3 sm:py-2 rounded-lg font-semibold hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200 flex items-center justify-center gap-2 text-base sm:text-sm touch-manipulation"
              >
                <FaRocket />
                Upgrade Now
              </button>
            )}
          </div>
        </div>

        {/* Mobile: Usage Dashboard First (most important info) */}
        <div className="lg:hidden mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">
            Your Stats
          </h2>
          
          <UsageDashboard
            usageStats={usageStats}
            subscription={subscription}
            onViewDetails={() => navigate('/subscription/manage')}
          />

          {/* Quick Upgrade Card - Mobile */}
          {tierInfo.name === 'Free' && (
            <div className="mt-4 bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-4">
              <h3 className="text-base font-bold text-gray-800 mb-2">
                Unlock All Features
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Upgrade to Premium for unlimited access to all AI tools, priority support, and advanced analytics.
              </p>
              <button
                onClick={handleUpgrade}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 active:scale-98 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 touch-manipulation"
              >
                View Plans
              </button>
            </div>
          )}
        </div>

        {/* Features Grid - Mobile Optimized Single Column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Features List */}
          <div className="lg:col-span-2">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
              Available Features
            </h2>
            
            {/* Single column on mobile for better readability */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {aiFeatures.map((feature) => (
                <FeatureCard
                  key={feature.id}
                  feature={feature}
                  isLocked={isFeatureLocked(feature.tier)}
                  tierBadge={feature.tier}
                  usageInfo={!isFeatureLocked(feature.tier) ? getUsageInfo(feature.usageKey) : null}
                  onClick={() => handleFeatureClick(feature)}
                />
              ))}
            </div>
          </div>

          {/* Desktop: Usage Dashboard */}
          <div className="hidden lg:block lg:col-span-1">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Your Stats
            </h2>
            
            <UsageDashboard
              usageStats={usageStats}
              subscription={subscription}
              onViewDetails={() => navigate('/subscription/manage')}
            />

            {/* Quick Upgrade Card - Desktop */}
            {tierInfo.name === 'Free' && (
              <div className="mt-6 bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Unlock All Features
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Upgrade to Premium for unlimited access to all AI tools, priority support, and advanced analytics.
                </p>
                <button
                  onClick={handleUpgrade}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200"
                >
                  View Plans
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
        requiredTier={selectedFeature?.tier || 'Premium'}
        featureName={selectedFeature?.name || 'this feature'}
      />

      <Footer />
    </div>
  );
};

export default AIFeaturesHub;
